import React, { useState, useEffect } from 'react';
import { Search, FileDown, Save, Calendar, CheckCircle2, XCircle, UserX, UserCheck, GraduationCap, ArrowRight, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function KardexSuspension() {
    const currentYear = new Date().getFullYear();
    const [gestion, setGestion] = useState(currentYear);
    const [estudiantes, setEstudiantes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [message, setMessage] = useState(null);

    // Local states for editing before save
    const [editedData, setEditedData] = useState({}); // { id_estudiante: { resolucion, estado } }

    useEffect(() => {
        loadEstudiantes();
    }, [gestion]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            loadEstudiantes();
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [search]);

    const loadEstudiantes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({ gestion });
            if (search) params.append('search', search);

            const response = await fetch(`${API_URL}/kardex/estudiantes-suspension?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setEstudiantes(data.estudiantes || []);

            // Reset edited data on load
            setEditedData({});
        } catch (error) {
            console.error('Error loading estudiantes:', error);
            setEstudiantes([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (id, field, value) => {
        setEditedData(prev => ({
            ...prev,
            [id]: {
                ...(prev[id] || {
                    resolucion: estudiantes.find(e => e.id_estudiante === id).resolucion || '',
                    estado: estudiantes.find(e => e.id_estudiante === id).estado_suspension
                }),
                [field]: value
            }
        }));
    };

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    const saveSuspension = async () => {
        if (!pendingAction) return;
        const { estudiante, data } = pendingAction;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/kardex/guardar-suspension`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    id_estudiante: estudiante.id_estudiante,
                    gestion: gestion,
                    resolucion: data.resolucion,
                    estado: data.estado
                })
            });

            if (response.ok) {
                setMessage({ type: 'success', text: `Estado de ${estudiante.nombres} actualizado` });
                loadEstudiantes(); // Refresh
                setTimeout(() => setMessage(null), 3000);
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al cargar los cambios' });
        } finally {
            setShowConfirmModal(false);
            setPendingAction(null);
        }
    };

    const handleSaveClick = (estudiante) => {
        const data = editedData[estudiante.id_estudiante];
        if (!data) return;

        // Si se está suspendiendo (estado 'suspendido'), pedir confirmación
        if (data.estado === 'suspendido') {
            setPendingAction({ estudiante, data });
            setShowConfirmModal(true);
        } else {
            // Si es reactivación ('activo'), guardar directamente según preferecia del usuario
            setPendingAction({ estudiante, data });
            setTimeout(() => { // Pequeño delay para asegurar que el state se use antes de llamar a save
                saveSuspensionDirectly(estudiante, data);
            }, 0);
        }
    };

    const saveSuspensionDirectly = async (estudiante, data) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/kardex/guardar-suspension`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    id_estudiante: estudiante.id_estudiante,
                    gestion: gestion,
                    resolucion: data.resolucion,
                    estado: data.estado
                })
            });

            if (response.ok) {
                setMessage({ type: 'success', text: `Estado de ${estudiante.nombres} actualizado` });
                loadEstudiantes();
                setTimeout(() => setMessage(null), 3000);
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al guardar' });
        }
    };

    const downloadExcel = () => {
        const headers = ["Codigo", "Nombres", "Apellidos", "CI", "Carrera", "Mención", "Resolución", "Fecha Suspensión", "Suspendido hasta", "Estado"];
        const data = estudiantes.map(e => {
            const fechaHasta = e.fecha_suspension && e.estado_suspension === 'suspendido' ? (() => {
                const d = new Date(e.fecha_suspension);
                d.setFullYear(d.getFullYear() + 1);
                return d.toLocaleDateString();
            })() : 'N/A';

            return [
                e.codigo_estudiante, e.nombres, e.apellidos, e.cedula_identidad,
                e.carrera_nombre, e.mencion_nombre || 'N/A',
                e.resolucion || 'N/A',
                e.fecha_suspension ? new Date(e.fecha_suspension).toLocaleDateString() : 'N/A',
                fechaHasta,
                e.estado_suspension === 'suspendido' ? 'Suspendido' : 'Activo'
            ];
        });

        const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Suspensiones");
        XLSX.writeFile(wb, `Reporte_Suspensiones_${gestion}.xlsx`);
    };

    return (
        <div className="p-6">
            {/* Action Bar Header */}
            <div className="mb-6">
                <div className="mb-6">
                    <h1 className="text-xl font-bold text-gray-800">Kardex &gt; Suspensión de Estudios</h1>
                    {/* <p className="text-slate-500 text-sm mt-1 italic opacity-80 font-medium tracking-tight">Administración de suspensiones temporales y bloqueos académicos.</p> */}
                </div>
                <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex flex-wrap gap-4 items-end">

                        <div className="relative">
                            {/* <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label> */}
                            <select
                                value={gestion}
                                onChange={(e) => setGestion(parseInt(e.target.value))}
                                className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                            >
                                {[currentYear, currentYear - 1, currentYear - 2].map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                            <span className="absolute right-3 top-[12px] pointer-events-none text-gray-400 text-xs">▼</span>
                        </div>
                        <div className="flex-grow flex items-end gap-2">
                            <div className="relative flex-grow">
                                {/* <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label> */}
                                <div className="relative">
                                    <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                    <input
                                        type="text"
                                        placeholder="Buscar..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={downloadExcel}
                                className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                                title="Descargar Excel">
                                <Download size={20} />
                            </button>

                            {message && (
                                <div className={`text-[10px] font-bold px-4 py-2 rounded-lg animate-in slide-in-from-left-2 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'
                                    }`}>
                                    {message.text}
                                </div>
                            )}

                        </div>

                    </div>
                </div>
                {/* <div className="flex flex-wrap items-center gap-4">
                    
                    <div className="flex items-center bg-white p-1 rounded-xl border border-slate-200 shadow-sm hover:border-slate-300 transition-all group">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-amber-500 transition-colors" size={15} />
                            <input
                                type="text"
                                placeholder="Buscar matriculado..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="bg-transparent border-none pl-9 pr-4 py-1.5 text-xs font-bold w-56 focus:ring-0 outline-none placeholder:text-slate-300 text-slate-600"
                            />
                        </div>
                    </div>

                    <div className="flex items-center bg-white p-1 rounded-xl border border-slate-200 shadow-sm hover:border-slate-300 transition-all">
                        <select
                            value={gestion}
                            onChange={(e) => setGestion(parseInt(e.target.value))}
                            className="bg-transparent border-none px-4 py-1.5 rounded-xl text-xs font-bold text-slate-500 focus:ring-0 cursor-pointer"
                        >
                            {[currentYear, currentYear - 1, currentYear - 2].map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>

                    <button
                        onClick={downloadExcel}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-black px-5 py-3 rounded-xl transition-all flex items-center gap-2 group shadow-md shadow-emerald-50 active:scale-95 border-b-2 border-emerald-700"
                    >
                        <FileDown size={14} />
                    </button>

                    {message && (
                        <div className={`text-[10px] font-bold px-4 py-2 rounded-lg animate-in slide-in-from-left-2 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'
                            }`}>
                            {message.text}
                        </div>
                    )}
                </div> */}
            </div>

            {/* Main Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">Estudiante / Código</th>
                                <th className="px-3 py-3 text-center font-semibold border">C.I.</th>
                                <th className="px-3 py-3 text-center font-semibold border">Carrera / Mención</th>
                                <th className="px-3 py-3 text-center font-semibold border">Info Resolutiva</th>
                                <th className="px-3 py-3 text-center font-semibold border">Suspendido hasta</th>
                                <th className="px-3 py-3 text-center font-semibold border">Estado / Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array(5).fill(0).map((_, i) => (
                                    <tr key={i} className="hover:bg-gray-50">
                                        <td colSpan={6} className="px-6 py-6"><div className="h-6 bg-slate-50 rounded-lg w-full"></div></td>
                                    </tr>
                                ))
                            ) : estudiantes.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-20 text-center text-slate-400 font-medium italic">No se encontraron estudiantes matriculados para la gestión {gestion}</td>
                                </tr>
                            ) : (
                                estudiantes.map((est) => {
                                    const isEdited = !!editedData[est.id_estudiante];
                                    const currentResolucion = isEdited ? editedData[est.id_estudiante].resolucion : (est.resolucion || '');
                                    const currentEstado = isEdited ? editedData[est.id_estudiante].estado : est.estado_suspension;

                                    return (
                                        <tr key={est.id_estudiante} className="hover:bg-gray-50">
                                            {/* Student Identity */}
                                            <td className="px-3 py-2 border">
                                                <div className="text-[9px] mb-0.5 ">{est.codigo_estudiante || 'N/A'}</div>
                                                <div className="font-bold ">{est.apellidos}, {est.nombres}</div>
                                            </td>

                                            {/* CI */}
                                            <td className="px-3 py-2 text-center border">
                                                <div className="text-xs">{est.cedula_identidad}</div>
                                            </td>

                                            {/* Academic Info */}
                                            <td className="px-3 py-2 text-center border">
                                                <div className="text-[11px] font-bold truncate max-w-[200px] mb-1">{est.carrera_nombre}</div>
                                                {est.mencion_nombre && (
                                                    <div className="text-[10px] text-slate-400 italic flex items-center gap-1 font-medium">
                                                        <ArrowRight size={10} className="text-slate-300" />
                                                        {est.mencion_nombre}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Resolution / Date */}
                                            <td className="px-3 py-2 text-center border">
                                                <div className="flex flex-col gap-2">
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="text"
                                                            placeholder="Nro Resolución..."
                                                            value={currentResolucion}
                                                            onChange={(e) => handleInputChange(est.id_estudiante, 'resolucion', e.target.value)}
                                                            className="text-[10px] font-bold bg-slate-50 border-slate-100 focus:border-blue-300 focus:ring-0 rounded-lg py-1 px-3 w-40 text-slate-600"
                                                        />
                                                    </div>
                                                    <div className="text-[9px] text-slate-400 flex items-center gap-1 font-mono uppercase">
                                                        <Calendar size={10} />
                                                        {est.fecha_suspension ? new Date(est.fecha_suspension).toLocaleDateString() : 'SIN REGISTRO'}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Suspensión Hasta */}
                                            <td className="px-3 py-2 text-center border">
                                                {currentEstado === 'suspendido' && est.fecha_suspension ? (
                                                    <div className="text-[10px] font-black text-slate-600 bg-slate-100 px-2 py-1 rounded-lg inline-block">
                                                        {(() => {
                                                            const d = new Date(est.fecha_suspension);
                                                            d.setFullYear(d.getFullYear() + 1);
                                                            return d.toLocaleDateString();
                                                        })()}
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-300 text-[10px] italic">N/A</span>
                                                )}
                                            </td>

                                            {/* Status / Save */}
                                            <td className="px-3 py-2 text-center border">
                                                <div className="flex items-center justify-center gap-4">
                                                    <button
                                                        onClick={() => handleInputChange(est.id_estudiante, 'estado', currentEstado === 'activo' ? 'suspendido' : 'activo')}
                                                        className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1.5 transition-colors ${currentEstado === 'activo'
                                                                ? 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100'
                                                                : 'bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100'
                                                            }`}
                                                    >
                                                        {currentEstado === 'activo' ? <UserCheck size={14} /> : <UserX size={14} />}
                                                        <span className="capitalize">{currentEstado}</span>
                                                    </button>

                                                    {isEdited && (
                                                        <button
                                                            onClick={() => handleSaveClick(est)}
                                                            className="w-8 h-8 rounded-xl bg-slate-800 text-white flex items-center justify-center hover:bg-black transition-all shadow-lg animate-in zoom-in-75 duration-200"
                                                            title="Guardar Cambios"
                                                        >
                                                            <Save size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full border border-slate-100 animate-in zoom-in-95 duration-200">
                        <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                            <UserX size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-slate-800 text-center mb-2">Confirmar Suspensión</h3>
                        <p className="text-slate-500 text-center text-sm mb-8 leading-relaxed">
                            ¿Está seguro que desea suspender a <span className="font-bold text-slate-700">{pendingAction?.estudiante?.nombres} {pendingAction?.estudiante?.apellidos}</span>?
                            <br /><br />
                            Esta acción bloqueará su acceso al sistema y lo marcará como inactivo para la gestión {gestion}.
                        </p>

                        <div className="flex gap-3">
                            <button
                                onClick={() => { setShowConfirmModal(false); setPendingAction(null); }}
                                className="flex-1 px-4 py-3 rounded-2xl text-sm font-bold text-slate-500 hover:bg-slate-50 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={saveSuspension}
                                className="flex-1 px-4 py-3 rounded-2xl bg-slate-800 text-white text-sm font-bold hover:bg-black transition-all shadow-lg shadow-slate-200"
                            >
                                Confirmar Suspensión
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}